import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

/**
 * Button - Glass Greenhouse Design System
 * 
 * Changes from default Shadcn:
 * - Rounded-full for default/destructive (pill shape)
 * - Active scale effect (btn-press)
 * - Softer shadows with glow on primary
 * - Slightly taller for better touch targets (44px on lg)
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-[0.98]",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground rounded-full shadow-md shadow-primary/20 hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/25 hover:-translate-y-0.5",
        destructive:
          "bg-destructive text-destructive-foreground rounded-full shadow-md hover:bg-destructive/90 hover:shadow-lg hover:-translate-y-0.5",
        outline:
          "border border-input bg-background rounded-xl shadow-soft-sm hover:bg-accent hover:text-accent-foreground hover:border-primary/30 hover:shadow-soft-md",
        secondary:
          "bg-secondary text-secondary-foreground rounded-xl shadow-soft-sm hover:bg-secondary/80 hover:shadow-soft-md",
        ghost: 
          "rounded-xl hover:bg-accent hover:text-accent-foreground",
        link: 
          "text-primary underline-offset-4 hover:underline",
        // New variant: Soft primary (green tint background)
        soft:
          "bg-primary/10 text-primary rounded-xl hover:bg-primary/20",
        // New variant: Amber/Warning
        warning:
          "bg-amber-500 text-white rounded-full shadow-md shadow-amber-500/20 hover:bg-amber-600 hover:shadow-lg hover:-translate-y-0.5",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-8 px-3 text-xs",
        lg: "h-11 px-6 text-base",
        icon: "h-10 w-10 rounded-xl",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
